import React, { useEffect, useRef } from 'react';
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { generateKey } from 'utils/helpers';

import * as S from './styles';

SwiperCore.use([Navigation, Pagination, Thumbs]);

type Props = {
  /** Images that will be shown on the slider */
  sliderImages: { image: string | React.ReactNode; imageAlt: string }[];
  /** Event fired when the active slide changes */
  onSlideChange?: () => void;
  /** Event fired when the active slide changes from a swipe action*/
  onSlideChangeFromSwipe?: () => void;
  /** Event fired for a click on the previous arrow */
  onPrevArrowClick?: () => void;
  /** Event fired for a click on the next arrow */
  onNextArrowClick?: () => void;
  /** Event fired for a click on a bullet. Index starts at 1 */
  onBulletClick?: (bulletIndex: number) => void;
};

/**
 * Slider image component for PDP
 * @param Props
 * @returns JSX.Element
 */
const PDPSwiperWrapper = ({
  sliderImages,
  onSlideChange,
  onSlideChangeFromSwipe,
  onPrevArrowClick,
  onNextArrowClick,
  onBulletClick,
}: Props): JSX.Element => {
  // Using a variable to track the last touch event. The swipper does not accept setState call as callbacks.
  let lastTouchEndEvent: MouseEvent | TouchEvent | PointerEvent | undefined =
    undefined;
  const prevSlideClass = 'prev-slide';
  const nextSlideClass = 'next-slide';
  const swiperRef = useRef<HTMLDivElement>(null);

  const pagination = {
    clickable: true,
    renderBullet: (_index: number, className: string) => {
      return `<span class="${className}"></span>`;
    },
  };

  const callSlideChangeEvent = () => {
    if (onSlideChange) {
      onSlideChange();
    }
  };

  const onRealIndexChange = () => {
    // If the last touch end is not undfined onSlideChangeFromSwipe
    if (lastTouchEndEvent) {
      callSlideChangeEvent();
      if (onSlideChangeFromSwipe) {
        onSlideChangeFromSwipe();
      }
    }
    // Reset last touch end event back to undefined
    lastTouchEndEvent = undefined;
  };

  const onTouchEnd = (
    _swiperCore: SwiperCore,
    event: MouseEvent | TouchEvent | PointerEvent,
  ) => {
    lastTouchEndEvent = event;
  };

  const onPrevClick = () => {
    callSlideChangeEvent();
    if (onPrevArrowClick) {
      onPrevArrowClick();
    }
  };

  const onNextClick = () => {
    callSlideChangeEvent();
    if (onNextArrowClick) {
      onNextArrowClick();
    }
  };

  const onBulletClickEvent = (index: number) => {
    if (onBulletClick) {
      onBulletClick(index + 1);
    }
  };

  useEffect(() => {
    const swiperBullets = swiperRef?.current?.querySelectorAll(
      '.swiper-pagination-bullet',
    );

    if (swiperBullets) {
      swiperBullets.forEach((bullet: Element, index: number) =>
        bullet.addEventListener('click', () => onBulletClickEvent(index)),
      );
    }

    return () => {
      if (swiperBullets) {
        swiperBullets.forEach((bullet: Element, index: number) =>
          bullet.removeEventListener('click', () => onBulletClickEvent(index)),
        );
      }
    };
  }, [swiperRef]);

  return (
    <>
      <S.MainContainer>
        <div ref={swiperRef}>
          <Swiper
            loop={sliderImages.length > 1}
            navigation={{
              prevEl: `.${prevSlideClass}`,
              nextEl: `.${nextSlideClass}`,
            }}
            onRealIndexChange={onRealIndexChange}
            onTouchEnd={onTouchEnd}
            pagination={pagination}
            // Needed to avoid briefly showing the last slide while loading
            slidesPerView="auto"
          >
            {sliderImages.map((sliderImage) =>
              typeof sliderImage.image === 'object' ? (
                <SwiperSlide key={generateKey()}>
                  {sliderImage.image}
                </SwiperSlide>
              ) : null,
            )}
          </Swiper>
        </div>
      </S.MainContainer>

      {/* Previous arrow */}
      <S.NavigationButton
        aria-label="Previous Slide"
        className={prevSlideClass}
        onClick={onPrevClick}
      >
        <S.LeftIcon />
      </S.NavigationButton>

      {/* Next arrow */}
      <S.NavigationButton
        aria-label="Next Slide"
        className={nextSlideClass}
        onClick={onNextClick}
      >
        <S.RightIcon />
      </S.NavigationButton>
    </>
  );
};

export default PDPSwiperWrapper;
